import {
    ApiCart,
    ApiCartItem,
    ApiClient,
    ApiShopperLandingPage,
    ApiShopperLandingPageLink,
    ApiSellingPlan,
    ApiSellingPlanAllocation,
    ApiSellingPlanGroup,
    CART_SOURCE_BACKEND,
    CART_SOURCE_FRONT_END,
    CART_SOURCE_KEY,
    MANAGED_GIFT_ATTRIBUTE,
    isApiSellingPlanFixedAmountPriceAdjustment,
    isApiSellingPlanFixedPriceAdjustment,
    isApiSellingPlanPercentagePriceAdjustment,
    VLPVariant,
} from '@storefronts/common-types'
import {
    AjaxShopifyCartData,
    CartItemStatus,
    isAjaxShopifyCartData,
    isApiCart,
    SellingPlan,
    SellingPlanAllocation,
    SellingPlanGroup,
    StockStatus,
    VariantWithImage,
} from '../types'
import { Cart, CartItem } from '../types'
import type { LandingPageModel } from '../../models/landingPageModel'
import {
    getBestDiscountsForProducts,
    pageDiscountStacksWith,
    promotionAppliesToProduct,
    promotionsMatch,
} from './discounts'
import { convertPriceToCents, getCartLinePrice } from './priceFormat'
import type { ApiPromotion } from '@storefronts/common-types'

import {
    cartHasActiveSubscriptions,
    getSellingPlanCompareAtPrice,
} from './subscriptions'
import { getFromStorageParsed, setInStorage } from '../storage'
import { findVariantWithImageForApiCartItem } from './userEventTools'
import { isOnShopDomain, transformLinkToVariant } from './helpers'
import {
    calculateItemAutomaticDiscountSavings,
    calculateItemDiscountCodeSavings,
    getItemAppliedDiscountCodeAllocations,
    getItemAutomaticDiscountAllocations,
} from './discountAllocations'

export {
    getItemAppliedDiscountCodes,
    calculateItemDiscountCodeSavings,
} from './discountAllocations'

export type ShopifyGraphQLErrors = {
    message: string
    graphQLErrors: {
        message: string
        path: (string | number)[]
        extensions: {
            code: string
            requiredAccess: string
        }
    }[]
}

export function isAccessScopeError(errorResponse: {
    isError: boolean
    data?: ShopifyGraphQLErrors
}) {
    return (
        errorResponse.isError &&
        (errorResponse.data?.graphQLErrors.some(
            ({ extensions }) => extensions.code === 'ACCESS_DENIED',
        ) ??
            false)
    )
}

export function getErrorMessage(errorResponse: {
    isError: boolean
    error?: string
    message?: string
    data?: ShopifyGraphQLErrors
}) {
    if (!errorResponse.isError) {
        return ''
    }
    if (errorResponse.data?.graphQLErrors) {
        const response = (errorResponse.data?.graphQLErrors ?? [])
            .map(({ message }) => message.trim())
            .join(', ')
            .trim()
        if (response?.length) {
            return response
        }
    }
    return errorResponse.error ?? errorResponse.message ?? ''
}

export function hasDeltas(deltas: {
    addedItems?: CartItem[]
    updatedItems?: CartItem[]
    deletedItems?: CartItem[]
}) {
    return !!(
        (deltas.addedItems && deltas.addedItems.length > 0) ||
        (deltas.updatedItems && deltas.updatedItems.length > 0) ||
        (deltas.deletedItems && deltas.deletedItems.length > 0)
    )
}

export function mergeDeferredAddItems(
    currentAddItems: CartItem[],
    newItemToAdd: CartItem,
) {
    const currentAddItemsClone = JSON.parse(
        JSON.stringify(currentAddItems),
    ) as CartItem[]
    // areMatchingCartItems keys on variant + selling plan for these
    // not-yet-synced entries.
    const item = currentAddItemsClone.find((item) =>
        areMatchingCartItems(newItemToAdd, item),
    )
    if (item) {
        item.quantity += newItemToAdd.quantity
    } else {
        currentAddItemsClone.unshift(newItemToAdd)
    }
    return currentAddItemsClone
}

export function hasShopifyCartLineId(
    item: Pick<CartItem, 'shopifyLineId'>,
): item is CartItem & { shopifyLineId: string } {
    return item.shopifyLineId != null && item.shopifyLineId !== ''
}

// Disable stepper/subscription controls only while a cart mutation is in
// flight. READY lines missing shopifyLineId must stay interactive so
// removeCartItem's local-only removal path remains reachable.
export function isCartItemEditDisabled(
    item: Pick<CartItem, 'status'>,
): boolean {
    return (
        item.status === CartItemStatus.ADDING ||
        item.status === CartItemStatus.UPDATING ||
        item.status === CartItemStatus.REMOVING
    )
}

export function isManagedGiftCartItem(
    item: Pick<CartItem, 'attributes'>,
): boolean {
    return item.attributes?.[MANAGED_GIFT_ATTRIBUTE] === 'true'
}

export function getCartItemAnimationKey(
    item: Pick<CartItem, 'variant' | 'attributes'>,
): string {
    const giftSuffix = isManagedGiftCartItem(item) ? ':gift' : ''
    return `${item.variant.shopifyId}${giftSuffix}`
}

export function getCartItemReactKey(
    item: Pick<CartItem, 'shopifyLineId' | 'variant' | 'attributes'>,
    items?: Pick<CartItem, 'shopifyLineId' | 'variant' | 'attributes'>[],
): string {
    const stableKey = getCartItemAnimationKey(item)
    if (!items?.length) {
        return stableKey
    }

    const stableKeyCount = items.filter(
        (other) => getCartItemAnimationKey(other) === stableKey,
    ).length
    if (stableKeyCount > 1 && hasShopifyCartLineId(item)) {
        return item.shopifyLineId
    }

    return stableKey
}

export type FindCartItemOptions = {
    sellingPlanId?: string | null
    shopifyLineId?: string
    excludeManagedGift?: boolean
}

export function findCartItem(
    items: CartItem[],
    variant: Pick<CartItem['variant'], 'shopifyId'>,
    options?: FindCartItemOptions,
): CartItem | undefined {
    const { sellingPlanId, shopifyLineId } = options ?? {}
    const excludeManagedGift = options?.excludeManagedGift ?? true

    if (shopifyLineId) {
        return items.find((item) => item.shopifyLineId === shopifyLineId)
    }

    return items.find((item) => {
        if (item.variant.shopifyId !== variant.shopifyId) {
            return false
        }
        if (
            sellingPlanId !== undefined &&
            (item.sellingPlanId ?? null) !== sellingPlanId
        ) {
            return false
        }
        if (excludeManagedGift && isManagedGiftCartItem(item)) {
            return false
        }
        return true
    })
}

export function sortCartItemsForDisplay(items: CartItem[]): CartItem[] {
    const regularItems: CartItem[] = []
    const giftItems: CartItem[] = []
    for (const item of items) {
        if (isManagedGiftCartItem(item)) {
            giftItems.push(item)
        } else {
            regularItems.push(item)
        }
    }
    return [...regularItems, ...giftItems]
}

export type CartItemAddInput = {
    variant: VariantWithImage
    quantity?: number
    sellingPlanId?: string | null
    attributes?: { [key: string]: string | null } | undefined
}

// cartLinesUpdate requires a non-null Shopify line id. Optimistic lines
// (still awaiting cartLinesAdd) must go through add, not update.
export function partitionCartItemsForShopifySync(
    variants: CartItemAddInput[],
    findExisting: (
        variant: VariantWithImage,
        sellingPlanId: string | null,
    ) => CartItem | undefined,
): { addedItems: CartItem[]; updatedItems: CartItem[] } {
    const addedItems: CartItem[] = []
    const updatedItems: CartItem[] = []

    for (const {
        variant,
        quantity = 1,
        sellingPlanId,
        attributes,
    } of variants) {
        const existingItem = findExisting(variant, sellingPlanId ?? null)
        if (existingItem && hasShopifyCartLineId(existingItem)) {
            existingItem.quantity += quantity
            updatedItems.push(existingItem)
            continue
        }
        addedItems.push({
            variant,
            quantity,
            sellingPlanId,
            attributes,
        })
    }

    return { addedItems, updatedItems }
}

export function mapCartItemsToApiLines(
    items?: CartItem[],
    isUpdateOperation: boolean = false,
) {
    if (!items) {
        return []
    }
    return items.flatMap((item) => {
        if (isUpdateOperation && !hasShopifyCartLineId(item)) {
            return []
        }
        return [
            {
                merchandiseId: `gid://shopify/ProductVariant/${item.variant.shopifyId}`,
                quantity: item.quantity,
                sellingPlanId: item.sellingPlanId,
                id: isUpdateOperation ? item.shopifyLineId : undefined,
                attributes: !isUpdateOperation ? item.attributes : undefined,
            },
        ]
    })
}

export function convertApiSellingPlanToSellingPlan(
    apiSellingPlan: ApiSellingPlan,
): SellingPlan {
    return {
        id: apiSellingPlan.id,
        name: apiSellingPlan.name,
        description: apiSellingPlan.description,
        priceAdjustments: apiSellingPlan.priceAdjustments.map(
            (priceAdjustment, index) => {
                let adjustmentValue
                if (
                    isApiSellingPlanFixedAmountPriceAdjustment(
                        priceAdjustment.adjustmentValue,
                    )
                ) {
                    const amount = convertPriceToCents(
                        priceAdjustment.adjustmentValue.adjustmentAmount.amount,
                    )
                    if (amount === undefined) {
                        // This should never happen
                        throw new Error(
                            `Invalid price adjustment in selling plan ${apiSellingPlan.name} at index ${index}`,
                        )
                    }
                    adjustmentValue = {
                        amount,
                    }
                } else if (
                    isApiSellingPlanFixedPriceAdjustment(
                        priceAdjustment.adjustmentValue,
                    )
                ) {
                    const price = convertPriceToCents(
                        priceAdjustment.adjustmentValue.price.amount,
                    )
                    if (price === undefined) {
                        // This should never happen
                        throw new Error(
                            `Invalid price adjustment in selling plan ${apiSellingPlan.name} at index ${index}`,
                        )
                    }
                    adjustmentValue = {
                        price,
                    }
                } else if (
                    isApiSellingPlanPercentagePriceAdjustment(
                        priceAdjustment.adjustmentValue,
                    )
                ) {
                    adjustmentValue = {
                        percentage:
                            priceAdjustment.adjustmentValue
                                .adjustmentPercentage,
                    }
                }
                if (!adjustmentValue) {
                    // This should never happen
                    throw new Error('Invalid price adjustment')
                }
                return {
                    adjustmentValue,
                    orderCount: priceAdjustment.orderCount ?? undefined,
                }
            },
        ),
        options: apiSellingPlan.options,
        recurringDeliveries: apiSellingPlan.recurringDeliveries,
    }
}

export function convertApiSellingPlanGroupToSellingPlanGroup(
    apiSellingPlanGroup: ApiSellingPlanGroup,
): SellingPlanGroup {
    return {
        appName: apiSellingPlanGroup.appName,
        id: apiSellingPlanGroup.id,
        name: apiSellingPlanGroup.name,
        options: apiSellingPlanGroup.options,
        sellingPlansIds:
            apiSellingPlanGroup.sellingPlans?.map(
                (sellingPlan) => sellingPlan.id,
            ) ?? [],
    }
}

export function convertApiSellingPlanAllocationToSellingPlanAllocation(
    apiSellingPlanAllocation: ApiSellingPlanAllocation,
): SellingPlanAllocation {
    return {
        priceAdjustments: (apiSellingPlanAllocation.priceAdjustments ?? []).map(
            (priceAdjustment) => {
                return {
                    compareAtPrice: convertPriceToCents(
                        priceAdjustment?.compareAtPrice?.amount,
                    ),
                    price: convertPriceToCents(priceAdjustment?.price?.amount),
                    perDeliveryPrice: convertPriceToCents(
                        priceAdjustment?.perDeliveryPrice?.amount,
                    ),
                    unitPrice: convertPriceToCents(
                        priceAdjustment?.unitPrice?.amount,
                    ),
                }
            },
        ),
        checkoutChargeAmount: convertPriceToCents(
            apiSellingPlanAllocation.checkoutChargeAmount?.amount,
        ),
        remainingBalanceChargeAmount: convertPriceToCents(
            apiSellingPlanAllocation.remainingBalanceChargeAmount?.amount,
        ),
        sellingPlan: convertApiSellingPlanToSellingPlan(
            apiSellingPlanAllocation.sellingPlan,
        ),
    }
}

export function isStubbedCartItem(item: CartItem): boolean {
    return !item?.variant?.link?.linkHash
}

export type CartItemPricingContext = {
    landingPageLinks?: ApiShopperLandingPageLink[]
    discount?: ApiPromotion
}

function getCatalogListPriceFromLink(
    link: ApiShopperLandingPageLink,
    variantShopifyId?: string,
): number {
    if (!variantShopifyId) {
        return link.price?.compareAtPrice ?? 0
    }

    const catalogVariant = link.variantInfo?.find(
        (entry) => entry.shopifyId === variantShopifyId,
    )
    if (catalogVariant) {
        return catalogVariant.price?.compareAtPrice ?? 0
    }

    return link.price?.compareAtPrice ?? 0
}

function getListPriceFromLineDiscountAllocations(item: CartItem): number {
    if (!item.discountAllocations?.length || item.quantity <= 0) {
        return 0
    }

    const lineDiscountTotal = calculateItemAutomaticDiscountSavings(item)

    if (lineDiscountTotal <= 0) {
        return 0
    }

    const finalPrice = item.variant.finalPrice ?? 0
    return finalPrice + Math.round(lineDiscountTotal / item.quantity)
}

// eslint-disable-next-line complexity
export function convertApiCartLineItemToCartItem(
    item: ApiCartItem,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    discount?: ApiPromotion,
): CartItem {
    const price = convertPriceToCents(item.variant.price?.price)
    const compareAtPrice = convertPriceToCents(
        item.variant.price?.compareAtPrice,
    )
    let finalPrice = convertPriceToCents(item.variant.discountedPrice)
    if (finalPrice === undefined) {
        finalPrice = price
    }

    // Some stores allow items to be added to cart even if they are
    // reported as out of stock.  So, only mark it as out of stock
    // if there is a quantity of zero
    const isOutOfStock = item.variant.currentlyNotInStock && item.quantity === 0

    // Fetch the variant if it's already known by the landingPage first in order to
    // have max compatibility with CartUpsells and other functionality.  Otherwise,
    // generate a stubbed version.
    const resolvedVariant = findVariantWithImageForApiCartItem(
        item,
        landingPageLinks,
        discount,
    )
    const catalogListPrice = resolvedVariant?.link
        ? getCatalogListPriceFromLink(
              resolvedVariant.link,
              item.variant.shopifyId,
          )
        : 0
    const mergedCompareAtPrice =
        Math.max(compareAtPrice ?? 0, catalogListPrice) || compareAtPrice

    const variant =
        resolvedVariant ??
        ({
            shopifyId: item.variant.shopifyId,
            title: item.variant.title,
            option1: item.variant.selectedOptions[0]?.value ?? null,
            option2: item.variant.selectedOptions[1]?.value ?? null,
            option3: item.variant.selectedOptions[2]?.value ?? null,
            featuredImage:
                item.variant.imageUrl ?? item.product.featuredImageUrl,
            imageUrl: item.variant.imageUrl ?? item.product.featuredImageUrl,
            link: {
                shopifyId: `${item.product.shopifyId}`,
                title: item.product.title,
                variantInfo: item.product.variantsCount > 1 ? [{}, {}] : [],
            } as ApiShopperLandingPageLink,
            // TBD: is this for subscriptions? If so, should we set based on sellingPlanInfo
            isSub: false,
        } as VariantWithImage)

    return {
        variant: {
            ...variant,
            // Even if using a known variant object, override the pricing, availability,
            // stock on what the cart gives us since it's more accurate.
            price: {
                price,
                compareAtPrice: mergedCompareAtPrice,
            },
            available:
                item.variant.availableForSale &&
                !isOutOfStock &&
                item.quantity > 0,
            stockStatus: isOutOfStock
                ? StockStatus.OUT_OF_STOCK
                : StockStatus.IN_STOCK,
            finalPrice,
            isSub: !!item.currentSellingPlanAllocation,
        } as VariantWithImage,
        sellingPlanId: item.currentSellingPlanAllocation?.sellingPlan.id,
        availableSellingPlanAllocations:
            item.availableSellingPlanAllocations?.map((allocation) =>
                convertApiSellingPlanAllocationToSellingPlanAllocation(
                    allocation,
                ),
            ),
        sellingPlanGroups: item.product.sellingPlanGroups?.map((group) =>
            convertApiSellingPlanGroupToSellingPlanGroup(group),
        ),
        quantity: item.quantity,
        shopifyLineId: item.shopifyLineId,
        attributes: item.attributes,
        discountAllocations: item.discountAllocations,
    }
}

export function convertApiCartToCart(
    apiCart: ApiCart,
    landingPageLinks: ApiShopperLandingPageLink[] = [],
    discount?: ApiPromotion,
): Cart {
    // Shopify returns the subtotal as the non-discounted amount
    // and the total as the discounted amount.
    // We're ignoring the totalTaxAmount since we're not calculating tax.
    const subtotalValue = convertPriceToCents(apiCart.cost?.subtotalAmount)
    const totalTaxAmountValue = convertPriceToCents(
        apiCart.cost?.totalTaxAmount,
    )
    const total = convertPriceToCents(apiCart.cost?.totalAmount)

    const subtotal =
        subtotalValue != null && totalTaxAmountValue != null
            ? Math.max(subtotalValue, totalTaxAmountValue)
            : (subtotalValue ?? totalTaxAmountValue)

    const actualDiscount =
        subtotal !== undefined && total !== undefined
            ? Math.max(0, subtotal - total)
            : undefined
    return {
        id: apiCart.id,
        checkoutUrl: apiCart.checkoutUrl,
        items:
            apiCart.lines?.map((item) =>
                convertApiCartLineItemToCartItem(
                    item,
                    landingPageLinks,
                    discount,
                ),
            ) ?? [],
        subtotal,
        total,
        discount: actualDiscount,
        attributes: apiCart.attributes,
        discountCodes: apiCart.discountCodes,
    }
}

// The same variant can be in the cart twice — as a one-time purchase and with
// a selling plan — so items match on variant + selling plan, where null and
// an omitted plan both mean "no selling plan". Managed gift lines are distinct
// from paid lines even when they share a variant. This intentionally does not
// use shopifyLineId so a just-added optimistic entry still matches its synced
// response line.
export function areMatchingCartItems(
    cartItem1?: CartItem,
    cartItem2?: CartItem,
) {
    return (
        cartItem1?.variant?.shopifyId === cartItem2?.variant?.shopifyId &&
        (cartItem1?.sellingPlanId ?? null) ===
            (cartItem2?.sellingPlanId ?? null) &&
        isManagedGiftCartItem(cartItem1 ?? {}) ===
            isManagedGiftCartItem(cartItem2 ?? {})
    )
}

// Generates a new Cart object based on:
// - The current cart object
// - The cart returned from the upsertCart API call
// - The deltas containing the items to be added, updated, and deleted as part of this particular upsertCart call
export function syncCartWithResponse(
    currentCart: Cart,
    responseCart: ApiCart,
    landingPage: LandingPageModel,
    deltas?: {
        addedItems?: CartItem[]
        updatedItems?: CartItem[]
        deletedItems?: CartItem[]
    },
) {
    const targetCart = JSON.parse(JSON.stringify(currentCart)) as Cart
    targetCart.checkoutUrl = responseCart.checkoutUrl

    // If we don't have the full cart object in the response, let's blank out the cart totals to force a derived calculation
    if (!responseCart.lines) {
        // Let's blank out the cart totals to force a derived calculation
        targetCart.subtotal = undefined
        targetCart.total = undefined
        targetCart.discount = undefined
        return targetCart
    }

    // If we have the full version of the cart in the response, let's use that as the
    // new cart to return but first we need to align a few things.
    const landingPageLinks = [
        ...(landingPage.landingPageLinks ?? []),
        ...(landingPage.cartUpsells ?? []),
    ]
    const apiCart = convertApiCartToCart(
        responseCart,
        landingPageLinks,
        landingPage.discount,
    )

    // Go through all of the items in the target cart sync the items' status.
    apiCart.items.forEach((item) => {
        const targetItem = targetCart.items.find((line) =>
            areMatchingCartItems(line, item),
        )
        if (targetItem) {
            item.status = targetItem.status
        }
    })

    // Let's add any items that are missing in the API Cart that are in the process being added
    // via another operation or are Out of Stock/Unavailable
    const missingItems = targetCart.items.filter(
        (item) =>
            !apiCart.items.some((apiItem) =>
                areMatchingCartItems(apiItem, item),
            ) &&
            (item.status === CartItemStatus.ADDING ||
                item.variant.stockStatus === StockStatus.OUT_OF_STOCK ||
                !item.variant.available ||
                deltas?.addedItems?.some((addedItem) =>
                    areMatchingCartItems(item, addedItem),
                )) &&
            !item.shopifyLineId,
    )

    if (deltas?.addedItems && deltas.addedItems.length > 0) {
        // Assume that any items that we attempted to add and were not included in the
        // response is either Out of Stock or unavailable.
        missingItems.forEach((item) => {
            if (
                deltas.addedItems &&
                deltas.addedItems.some((addedItem) =>
                    areMatchingCartItems(addedItem, item),
                )
            ) {
                item.variant.available = false
                item.variant.stockStatus = StockStatus.OUT_OF_STOCK
                item.quantity = 0
            }
        })
    }
    if (missingItems.length > 0) {
        apiCart.items.push(...missingItems)
    }

    const mapCartItems = apiCart.items.reduce(
        (map, cartItem) => {
            let line = map[cartItem.variant.shopifyId]
            if (!line) {
                line = []
                map[cartItem.variant.shopifyId] = line
            }
            line.push(cartItem)
            return map
        },
        {} as Record<string, CartItem[]>,
    )

    Object.keys(mapCartItems).forEach((key) => {
        const updatingItem =
            deltas?.updatedItems?.find(
                (updatedItem) => key === updatedItem.variant.shopifyId,
            ) ??
            deltas?.addedItems?.find(
                (addedItem) => key === addedItem.variant.shopifyId,
            )
        if (updatingItem) {
            const items = mapCartItems[key]
            const totalQuantity = items.reduce(
                (total, item) => total + item.quantity,
                0,
            )
            const isAnyOutOfStock = items.some(
                ({ variant }) =>
                    !variant.stockStatus ||
                    variant.stockStatus !== StockStatus.IN_STOCK,
            )
            const itemToUpdate =
                items.find(
                    (item) => updatingItem.shopifyLineId === item.shopifyLineId,
                ) ?? items[0]
            if (
                updatingItem.quantity > totalQuantity &&
                !isAnyOutOfStock &&
                !isManagedGiftCartItem(itemToUpdate) &&
                !isManagedGiftCartItem(updatingItem)
            ) {
                itemToUpdate.variant.stockStatus = StockStatus.LIMITED_STOCK
            }
        }
    })

    // Clear the status of the items that should have been taken care of via the deltas.
    if (deltas) {
        clearCartItemStatus(apiCart, deltas)
    }

    // Now lets sort the items in the API cart to match the order of the target cart.
    apiCart.items.sort((a, b) => {
        const aIndex = targetCart.items.findIndex((item) =>
            areMatchingCartItems(item, a),
        )
        const bIndex = targetCart.items.findIndex((item) =>
            areMatchingCartItems(item, b),
        )
        if (aIndex === -1) {
            return -1
        }
        if (bIndex === -1) {
            return 1
        }
        return aIndex - bIndex
    })

    return apiCart
}

// Sets the status of the items in the cart based on the deltas.
export function setCartItemStatus(
    cart: Cart,
    {
        deletedItems,
        addedItems,
        updatedItems,
    }: {
        deletedItems?: CartItem[]
        addedItems?: CartItem[]
        updatedItems?: CartItem[]
    },
) {
    cart.items.forEach((item) => {
        if (
            deletedItems &&
            deletedItems.some((deletedItem) =>
                areMatchingCartItems(deletedItem, item),
            )
        ) {
            item.status = CartItemStatus.REMOVING
        } else if (
            addedItems &&
            addedItems.some((addedItem) =>
                areMatchingCartItems(addedItem, item),
            )
        ) {
            item.status = CartItemStatus.ADDING
        } else if (
            updatedItems &&
            updatedItems.some((updatedItem) =>
                areMatchingCartItems(updatedItem, item),
            )
        ) {
            item.status = CartItemStatus.UPDATING
        }
    })
}

// Clears the status of the items in the cart based on the deltas.
export function clearCartItemStatus(
    cart: Cart,
    {
        deletedItems,
        addedItems,
        updatedItems,
    }: {
        deletedItems?: CartItem[]
        addedItems?: CartItem[]
        updatedItems?: CartItem[]
    },
) {
    cart.items.forEach((item) => {
        if (
            deletedItems &&
            deletedItems.some((deletedItem) =>
                areMatchingCartItems(deletedItem, item),
            )
        ) {
            item.status = CartItemStatus.READY
        } else if (
            addedItems &&
            addedItems.some((addedItem) =>
                areMatchingCartItems(addedItem, item),
            )
        ) {
            item.status = CartItemStatus.READY
        } else if (
            updatedItems &&
            updatedItems.some((updatedItem) =>
                areMatchingCartItems(updatedItem, item),
            )
        ) {
            item.status = CartItemStatus.READY
        }
    })
}

export const buildCartVariant = (
    activeVariant: VLPVariant | null,
    link: ApiShopperLandingPageLink,
    pageDiscount: ApiPromotion | null,
    isSub = false,
): VariantWithImage => ({
    ...(activeVariant
        ? {
              ...activeVariant,
              imageUrl: activeVariant.featuredImage || link.imageUrl,
              link,
          }
        : transformLinkToVariant(link)),
    finalPrice: getCartLinePrice(link, pageDiscount, activeVariant),
    isSub,
})

/**
 * Effective fixed page discount to apply to cart total (cents).
 * Returns 0 if there is no fixed page discount, or if any line item has
 * a non-combinable product discount applied (so we do not double-apply).
 * Uses each item's variant.link (ApiShopperLandingPageLink) for discount context.
 */
export function getEffectiveFixedDiscountForCart(
    cart: Cart,
    pageDiscount: ApiPromotion | null,
): number {
    if (!pageDiscount) return 0
    if (pageDiscount.discountType?.toLowerCase() !== 'fixed') return 0
    if (cart.items.length === 0) return 0

    // A product-specific discount only reduces the total when the cart holds
    // an eligible item; checkout won't apply it otherwise. NONE scope applies
    // cart-wide when the discount is order-level, but a shipping-only code
    // never reduces the merchandise subtotal.
    if (
        pageDiscount.productScope === 'SPECIFIC' &&
        !cart.items.some((item) =>
            promotionAppliesToProduct(
                pageDiscount,
                item.variant.link?.shopifyId,
            ),
        )
    ) {
        return 0
    }
    if (
        pageDiscount.productScope === 'NONE' &&
        pageDiscount.isOrderLevel === false
    ) {
        return 0
    }

    // When the cart already carries discount-code allocations, Shopify has
    // applied the code and the savings are baked into each line's finalPrice —
    // subtracting the fixed value again would double-count it in the subtotal.
    const codeAlreadyAllocated = cart.items.some(
        (item) => calculateItemDiscountCodeSavings(item) > 0,
    )
    if (codeAlreadyAllocated) return 0

    // Automatic allocations are likewise baked into finalPrice; a
    // non-combining page code won't stack with them at checkout.
    if (
        !pageDiscount.combinesWithProductDiscounts &&
        cart.items.some(
            (item) => getItemAutomaticDiscountAllocations(item).length > 0,
        )
    ) {
        return 0
    }

    const anyNonCombinableProductDiscountApplied = cart.items.some(
        ({ variant }) => {
            const link = variant.link
            // No ApiShopperLandingPageLink associated to the variant, assume no product discounts.
            if (!link) return false

            const bestDiscounts = getBestDiscountsForProducts(
                link,
                pageDiscount,
                variant.price?.price,
            )

            // If no bestDiscount is returned then assume no product discounts
            // If more than 1 bestDiscount is returned, then assume the discounts are combinable and
            // can proceed.
            if (!bestDiscounts || bestDiscounts.length !== 1) return false

            // If we're here, we're down to one discount.
            const bestDiscount = bestDiscounts[0]

            // If bestDiscount matches the pageDiscount then we can proceed.
            if (promotionsMatch(bestDiscount, pageDiscount)) return false

            // If the product discount is non-combinable but a fixed discount, then it was not
            // calculated as part of the item's price.
            if (bestDiscount.discountType.toLowerCase() === 'fixed')
                return false

            // The applied percent discount only blocks the page discount when
            // Shopify's combinability rules say the pair can't stack.
            return !pageDiscountStacksWith(pageDiscount, bestDiscount)
        },
    )

    if (anyNonCombinableProductDiscountApplied) return 0
    const lineTotal = cart.items.reduce(
        (sum, item) => sum + (item.variant.finalPrice ?? 0) * item.quantity,
        0,
    )
    return Math.min(pageDiscount.discountValue ?? 0, lineTotal)
}

export function getDiscountedCartTotal(
    cart: Cart,
    useCartsTotal: boolean,
    fixedDiscount: number = 0,
) {
    let total = 0
    cart.items.forEach((item) => {
        total += item.variant.finalPrice * item.quantity
    })
    const totalFromContents = Math.max(0, total - Math.max(fixedDiscount, 0))
    if (useCartsTotal && cart.total !== undefined) {
        // [sc-27913] items that are "not available in the user's location"
        // gets added to the cart, do not get returned as line items *but*
        // they still affect the totals.
        // There is no clear indication this has happened so to minimize this
        // let's ignore the cart.total if it's larger than the amount derived by the
        // cart's contents.
        return Math.min(cart.total, totalFromContents)
    }
    return totalFromContents
}

export function getUnDiscountedCartTotal(
    cart: Cart,
    useCartsTotal: boolean,
    pricingContext?: CartItemPricingContext,
) {
    let totalFromContents = 0
    cart.items.forEach((item) => {
        totalFromContents += getListPrice(item, pricingContext) * item.quantity
    })

    if (
        useCartsTotal &&
        cart.subtotal !== undefined &&
        // Discounts from subscriptions are already in the subtotal
        !cartHasActiveSubscriptions(cart)
    ) {
        // [sc-27913] items that are "not available in the user's location"
        // gets added to the cart, do not get returned as line items *but*
        // they still affect the totals.
        // There is no clear indication this has happened so to minimize this
        // let's ignore the cart.subtotal if it's larger than the amount derived by the
        // cart's contents.
        return Math.min(cart.subtotal, totalFromContents)
    }
    return totalFromContents
}

/**
 * Returns the highest List Price for a particular line item.
 * @returns number
 */
function getCatalogListPrice(
    item: CartItem,
    pricingContext?: CartItemPricingContext,
): number {
    let maxCatalogListPrice = 0
    const productId = item.variant.link?.shopifyId

    if (item.variant.link) {
        maxCatalogListPrice = Math.max(
            maxCatalogListPrice,
            getCatalogListPriceFromLink(
                item.variant.link,
                item.variant.shopifyId,
            ),
        )
    }

    if (pricingContext?.landingPageLinks?.length && productId) {
        const resolvedLink = pricingContext.landingPageLinks.find(
            (link) => link.shopifyId === productId,
        )
        if (resolvedLink) {
            maxCatalogListPrice = Math.max(
                maxCatalogListPrice,
                getCatalogListPriceFromLink(
                    resolvedLink,
                    item.variant.shopifyId,
                ),
            )
        }
    }

    return maxCatalogListPrice
}

export function getListPrice(
    item: CartItem,
    pricingContext?: CartItemPricingContext,
) {
    return Math.max(
        item.variant.price.price ?? 0,
        item.variant.price.compareAtPrice ?? 0,
        getCatalogListPrice(item, pricingContext),
        getSellingPlanCompareAtPrice(item) ?? 0,
        getListPriceFromLineDiscountAllocations(item),
    )
}

/**
 * Returns the list price for a particular line item if it is greater then
 * the current price.  Otherwise returns null
 * @returns number | null
 */
export function getEffectiveListPrice(
    item: CartItem,
    pricingContext?: CartItemPricingContext,
): number | null {
    const maxComparePrice = getListPrice(item, pricingContext)
    return maxComparePrice > item.variant.finalPrice ? maxComparePrice : null
}

/**
 * Returns total of all of the savings and discounts for a specifc line item
 * inclusive of item quantity (not / single item value)
 * @returns number
 */
export function calculateItemTotalSavings(
    item: CartItem,
    pricingContext?: CartItemPricingContext,
): number {
    const comparePrice = getListPrice(item, pricingContext)
    const finalPrice = item.variant.finalPrice ?? 0
    if (comparePrice > finalPrice) {
        return (comparePrice - finalPrice) * item.quantity
    }
    return 0
}

// Called from within the QueryParamContenxt and does not have the LandignPageModel yet.
export function isShopCartDefault(
    landingPage?: ApiShopperLandingPage | null,
): boolean {
    const shopsWithCartDefault =
        process.env.NEXT_PUBLIC_SHOP_NAMES_WITH_STOREFRONT_CART_DEFAULT?.split(
            ';',
        )?.map((shop) => shop.trim()) || []
    return !shopsWithCartDefault.includes(
        landingPage?.client?.shopifyShop ?? '',
    )
}

function getRecoveredCartKeyForSharedCart(
    clientHash: string,
    currentCartId: string,
) {
    if (!currentCartId) {
        return null
    }
    if (!clientHash) {
        return null
    }
    // Simplify the shopGuiID
    const cartId = currentCartId.split('/').pop() ?? ''
    return `__vd_cart_rec_alt__${clientHash}_${cartId}`
}

export function isRecoveredCartInSharedCart(
    clientHash: string,
    currentCartId: string,
    recoveredCart: ApiCart | AjaxShopifyCartData | null,
) {
    if (!recoveredCart || !clientHash || !currentCartId) {
        return null
    }
    const alternativeCartRecoveredKey = getRecoveredCartKeyForSharedCart(
        clientHash,
        currentCartId,
    )
    if (!alternativeCartRecoveredKey) {
        return null
    }
    let cartId: string | undefined
    if (isApiCart(recoveredCart)) {
        cartId = recoveredCart.id
    } else if (isAjaxShopifyCartData(recoveredCart)) {
        cartId = recoveredCart.token
    }
    if (!cartId) {
        return null
    }
    const restoredCookie = getFromStorageParsed(alternativeCartRecoveredKey)
    if (!restoredCookie) {
        return false
    }
    return restoredCookie.split(',').includes(cartId)
}
export function setRecoveredCartInSharedCart(
    clientHash: string,
    currentCartId: string,
    recoveredCart: ApiCart | AjaxShopifyCartData | null,
) {
    let cartId: string | undefined
    if (isApiCart(recoveredCart)) {
        cartId = recoveredCart.id
    } else if (isAjaxShopifyCartData(recoveredCart)) {
        cartId = recoveredCart.token
    }
    if (!cartId) {
        return null
    }
    const alternativeCartRecoveredKey = getRecoveredCartKeyForSharedCart(
        clientHash,
        currentCartId,
    )
    if (!alternativeCartRecoveredKey) {
        return null
    }
    let restoredCookie = getFromStorageParsed(alternativeCartRecoveredKey) ?? ''
    restoredCookie += `${restoredCookie.trim().length ? ',' : ''}${cartId}`
    setInStorage(alternativeCartRecoveredKey, restoredCookie)
}

export const forTesting = {
    getRecoveredCartKeyForSharedCart,
    getItemAppliedDiscountCodeAllocations,
}

export function getCheckoutUrl(
    client: ApiClient,
    cart: Cart,
    discountCode?: string,
    logPrefix?: string,
) {
    const cartCheckoutUrl = cart.checkoutUrl
    const onShopDomain = isOnShopDomain(client)
    const cartSource = cart.attributes?.[CART_SOURCE_KEY]
    const sanitizedLogPrefix = `${logPrefix ? `${logPrefix} ` : ''}${!onShopDomain ? "(not on shop's domain) - " : ''} cart source (${cartSource ?? 'not set'}) - `

    // If the cart has a checkoutUrl and not created using the backend storefront API, then
    // assume that the cart is created in a way that provides an email confirmation based on
    // cart source value.
    // 1. CART_SOURCE_FRONT_END: created from the shopper-frontend using the storefronts tokenless API.
    // 2. <no value>: assume cart created from the online shop without that attributes there.
    if (cartCheckoutUrl && cartSource !== CART_SOURCE_BACKEND) {
        const cartSourceText =
            cartSource === CART_SOURCE_FRONT_END
                ? 'created on frontend using tokenless API'
                : !cartSource
                  ? 'created on online shop'
                  : `unknown source ${cartSource}`
        console.log(
            `${sanitizedLogPrefix}cart ${cartSourceText} directing to our cart's checkout URL.`,
        )
        return cartCheckoutUrl
    }

    // Cart created from our backend or missing checkoutUrl
    // Fallback using permalinks.
    const permalinkUrl = _generateCartPermalink(
        client.mainDomain ?? client.shopifyShop ?? '',
        cart,
        discountCode,
        client.shopifyShop,
    )
    if (
        // simple permalink works everywhere
        !permalinkUrl.includes('/cart/clear') ||
        // /cart/clear only works if on shop domain so fails in preview, dev or admin-frontend
        onShopDomain
    ) {
        console.warn(`${sanitizedLogPrefix}Falling back to a cart permalink`)
        return permalinkUrl
    }

    // If the full permalink is incompatible and we're missing a cartCheckoutUrl, then let's
    // just fallback to a simple permalink.
    if (!cartCheckoutUrl) {
        console.warn(
            `${sanitizedLogPrefix}Missing URL from the cart. Falling back to a simple permalink.`,
        )
        return generateSimpleCartPermalink(
            client.mainDomain ?? client.shopifyShop ?? '',
            cart.items,
            discountCode,
        )
    }

    // We can't get a full permalink working, chances are we're in a dev, preview or admin-frontend
    // case so the confirmation email is a non-issue.  We can just use the cartCheckoutUrl.
    console.warn(
        `${sanitizedLogPrefix}Could not use the permalink fallback. Using the URL from the cart.`,
    )
    return cartCheckoutUrl
}

export function _generateCartPermalinkWithSellingPlanIds(
    domain: string,
    items: CartItem[],
    discountCode?: string,
) {
    const itemQueryParam = items
        .map(
            (item) =>
                `items[][id]=${item.variant.shopifyId}&items[][quantity]=${item.quantity}${item.sellingPlanId ? `&items[][selling_plan]=${item.sellingPlanId.replace('gid://shopify/SellingPlan/', '')}` : ''}`,
        )
        .join('&')
    return `https://${domain}/cart/clear?return_to=/cart/add${encodeURIComponent(`?${itemQueryParam}${discountCode ? `&discount=${discountCode}` : ''}&return_to=/checkout`)}`
}

export function generateSimpleCartPermalink(
    domain: string,
    items: CartItem[],
    discountCode?: string,
) {
    const itemQueryParam = items
        .map((item) => `${item.variant.shopifyId}:${item.quantity}`)
        .join(',')
    return `https://${domain}/cart/${itemQueryParam}${discountCode ? `?discount=${discountCode}` : ''}`
}

export function _generateCartPermalink(
    domain: string,
    cart: Cart,
    discountCode?: string,
    shopifyDomain?: string,
) {
    const items = cart.items.filter(
        (item) =>
            // Remove items that are being removed and have no quantity
            // or are unavailable or out of stock.
            item.status !== CartItemStatus.REMOVING &&
            item.quantity > 0 &&
            item.variant.available &&
            item.variant.stockStatus !== StockStatus.OUT_OF_STOCK,
    )
    // if there are selling plan Ids then need the alternative selling plan url
    // but don't favor this approach as it's a little flakey
    if (items.some((item) => !!item.sellingPlanId)) {
        return _generateCartPermalinkWithSellingPlanIds(
            shopifyDomain ?? domain,
            items,
            discountCode,
        )
    }
    return generateSimpleCartPermalink(domain, items, discountCode)
}
